import { useEffect, useId, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Button } from "./Button";

/**
 * Gráfico SVG simples (barras agrupadas ou linhas), sem biblioteca.
 * Regras (skill dataviz): um eixo só; cores categóricas em ordem fixa (--chart-1..3, validadas);
 * texto em tokens de texto; legenda sempre que houver 2+ séries; tooltip no hover/foco;
 * visão em tabela disponível.
 */

export interface ChartSeries {
  name: string;
  values: Array<number | null>;
}

export interface ChartProps {
  type: "bar" | "line";
  labels: string[];
  series: ChartSeries[];
  format: (v: number) => string;
  /** Formato curto para o eixo (ex.: R$ 12 mil). */
  formatAxis?: (v: number) => string;
  height?: number;
  title: string;
  /** Conteúdo acima do gráfico, ao lado da legenda. */
  aside?: ReactNode;
}

const COLORS = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)"];

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}

function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [w, setW] = useState(640);
  useEffect(() => {
    if (!el) return;
    setW(Math.max(260, Math.floor(el.getBoundingClientRect().width)));
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const cw = entries[0]?.contentRect.width;
      if (cw) setW(Math.max(260, Math.floor(cw)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, w];
}

export function Chart({ type, labels, series, format, formatAxis = format, height = 220, title, aside }: ChartProps) {
  const [wrapRef, width] = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const id = useId();
  const shown = series.slice(0, 3);

  const { min, max } = useMemo(() => {
    let lo = 0;
    let hi = 0;
    for (const s of shown) for (const v of s.values) if (v !== null) {
      lo = Math.min(lo, v);
      hi = Math.max(hi, v);
    }
    const top = niceMax(hi);
    const bottom = lo < 0 ? -niceMax(-lo) : 0;
    return { min: bottom, max: top };
  }, [shown]);

  const padL = 64;
  const padR = 12;
  const padT = 10;
  const padB = 26;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const n = Math.max(labels.length, 1);
  const band = innerW / n;
  const y = (v: number) => padT + ((max - v) / (max - min || 1)) * innerH;
  const zeroY = y(0);
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => min + (max - min) * t);
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(innerW / 64))));

  if (labels.length === 0) return <p className="muted">Sem dados no período.</p>;

  return (
    <figure style={{ margin: 0 }} aria-labelledby={`${id}-t`}>
      <figcaption id={`${id}-t`} className="sr-only">
        {title}
      </figcaption>
      <div className="vx-row" style={{ marginBottom: 6 }}>
        {shown.length > 1 ? (
          <div className="vx-legend" aria-hidden={asTable}>
            {shown.map((s, i) => (
              <span key={s.name}>
                <i style={{ background: COLORS[i] }} />
                {s.name}
              </span>
            ))}
          </div>
        ) : null}
        <span className="vx-spacer" />
        {aside}
        <Button size="sm" variant="ghost" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
          {asTable ? "Ver gráfico" : "Ver tabela"}
        </Button>
      </div>
      {asTable ? (
        <div className="vx-table-wrap">
          <table className="vx-table">
            <caption className="sr-only">{title}</caption>
            <thead>
              <tr>
                <th scope="col">Período</th>
                {shown.map((s) => (
                  <th key={s.name} scope="col" className="is-right">
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((l, i) => (
                <tr key={l}>
                  <th scope="row" style={{ fontWeight: 500 }}>
                    {l}
                  </th>
                  {shown.map((s) => (
                    <td key={s.name} className="is-right">
                      {s.values[i] === null || s.values[i] === undefined ? "-" : format(s.values[i]!)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div ref={wrapRef} style={{ position: "relative" }} onMouseLeave={() => setHover(null)}>
          <svg className="vx-chart" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
            {ticks.map((t) => (
              <g key={t}>
                <line className="grid" x1={padL} x2={width - padR} y1={y(t)} y2={y(t)} />
                <text x={padL - 8} y={y(t) + 4} textAnchor="end">
                  {formatAxis(t)}
                </text>
              </g>
            ))}
            <line className="axis" x1={padL} x2={width - padR} y1={zeroY} y2={zeroY} />
            {labels.map((l, i) =>
              i % labelEvery === 0 ? (
                <text key={l} x={padL + band * i + band / 2} y={height - 8} textAnchor="middle">
                  {l}
                </text>
              ) : null,
            )}
            {hover !== null ? (
              <rect x={padL + band * hover} y={padT} width={band} height={innerH} fill="var(--surface-2)" opacity={0.7} />
            ) : null}
            {type === "bar"
              ? shown.map((s, si) => {
                  const groupW = Math.min(band * 0.72, 22 * shown.length + 2 * (shown.length - 1));
                  const barW = (groupW - 2 * (shown.length - 1)) / shown.length;
                  return s.values.map((v, i) => {
                    if (v === null || v === 0) return null;
                    const x = padL + band * i + (band - groupW) / 2 + si * (barW + 2);
                    const top = y(Math.max(v, 0));
                    const bottom = y(Math.min(v, 0));
                    const h = Math.max(1, bottom - top);
                    const r = Math.min(4, barW / 2, h);
                    const d =
                      v >= 0
                        ? `M${x},${bottom} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${bottom} Z`
                        : `M${x},${top} V${bottom - r} Q${x},${bottom} ${x + r},${bottom} H${x + barW - r} Q${x + barW},${bottom} ${x + barW},${bottom - r} V${top} Z`;
                    return (
                      <path
                        key={`${si}-${i}`}
                        className={v >= 0 ? "vx-chart__bar" : "vx-chart__bar vx-chart__bar--neg"}
                        style={{ "--i": i } as CSSProperties}
                        d={d}
                        fill={COLORS[si]}
                      />
                    );
                  });
                })
              : shown.map((s, si) => {
                  const pts = s.values
                    .map((v, i) => (v === null ? null : `${padL + band * i + band / 2},${y(v)}`))
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <g key={s.name}>
                      <polyline className="vx-chart__line" pathLength={1} points={pts} fill="none" stroke={COLORS[si]} strokeWidth={2} strokeLinejoin="round" />
                      {hover !== null && s.values[hover] !== null && s.values[hover] !== undefined ? (
                        <circle cx={padL + band * hover + band / 2} cy={y(s.values[hover]!)} r={4.5} fill={COLORS[si]} stroke="var(--surface)" strokeWidth={2} />
                      ) : null}
                    </g>
                  );
                })}
            {labels.map((l, i) => (
              <rect
                key={`hit-${l}`}
                x={padL + band * i}
                y={padT}
                width={band}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
              />
            ))}
          </svg>
          {hover !== null ? (
            <div
              role="status"
              style={{
                position: "absolute",
                top: 4,
                left: Math.min(Math.max(padL + band * hover + band / 2 + 10, 0), width - 200),
                minWidth: 170,
                pointerEvents: "none",
                background: "var(--surface)",
                border: "1px solid var(--border-strong)",
                borderRadius: "var(--radius)",
                boxShadow: "var(--shadow-pop)",
                padding: "6px 10px",
                fontSize: "var(--fs-12)",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 2 }}>{labels[hover]}</div>
              {shown.map((s, si) => (
                <div key={s.name} className="vx-row" style={{ gap: 6, flexWrap: "nowrap" }}>
                  <i style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[si], display: "inline-block" }} />
                  <span className="text-2">{s.name}</span>
                  <span className="vx-spacer" />
                  <span className="num">{s.values[hover] === null || s.values[hover] === undefined ? "-" : format(s.values[hover]!)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </figure>
  );
}
